import {
  Event,
  createEvent,
  Effect,
  createStore,
  attach,
  sample,
  split,
  StoreWritable,
  EventCallable,
  merge
} from 'effector'
import { persist } from 'effector-storage/local'

export class Permission {
  readonly name: PermissionName
  readonly permissionCheckRequest: EventCallable<void> = createEvent<void>()

  readonly permissionGranted: Event<void>
  readonly permissionDenied: Event<void>

  readonly permissionChanged: EventCallable<PermissionStatus> =
    createEvent<PermissionStatus>()
  readonly $state: StoreWritable<PermissionState | null>

  private readonly permissionRequestFx: Effect<void, PermissionStatus, Error>

  private removeListener: VoidFunction | undefined

  constructor(
    name: PermissionName,
    checkPermissionFx: Effect<PermissionName, PermissionStatus, Error>,
    startPermissionsService: Event<void>
  ) {
    this.name = name

    this.$state = createStore<PermissionState | null>(null)

    persist({
      store: this.$state,
      key: `${name}_permission_state`,
      pickup: startPermissionsService
    })

    this.permissionRequestFx = attach({
      effect: checkPermissionFx,
      mapParams: (): PermissionName => name
    })

    sample({
      clock: this.permissionCheckRequest,
      target: this.permissionRequestFx
    })

    const permissionUpdated = merge([
      this.permissionRequestFx.doneData,
      this.permissionChanged
    ])

    this.permissionRequestFx.doneData.watch((permission) => {
      const handleChange = () => {
        this.permissionChanged(permission)
      }

      this.removeListener?.()
      permission.addEventListener('change', handleChange)

      this.removeListener = () => {
        permission.removeEventListener('change', handleChange)
      }
    })

    this.$state.on(permissionUpdated, (_, state) => state.state)

    const { granted, denied } = split(permissionUpdated, {
      granted: ({ state }) => state === 'granted',
      denied: ({ state }) => state === 'denied'
    })

    this.permissionGranted = granted.map(() => void 0)
    this.permissionDenied = denied.map(() => void 0)
  }

  destroy = (): void => {
    this.removeListener?.()
  }
}
